import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScheduleSpecificDateApplyRequest } from "./scheduleSpecificDateApply";

export interface ScheduleSpecificDateRequestState<
    TRequest extends ScheduleSpecificDateApplyRequest,
> {
    request: TRequest | null;
    /** Opens the modal; resolves to whether the schedule was applied once it closes. */
    open: (request: TRequest) => Promise<boolean>;
    /** The apply for `request` landed: closes the modal and settles its `open` with true. Ignored once another request replaced it. */
    complete: (request: TRequest) => void;
    /** The user closed the modal without applying: settles `open` with false. */
    cancel: () => void;
}

/**
 * The pending Specific date request behind the modal. `open` resolves only
 * when the modal closes, so a replace flow that already removed the previous
 * schedule learns whether a new one was applied.
 */
export function useScheduleSpecificDateRequest<
    TRequest extends ScheduleSpecificDateApplyRequest,
>(): ScheduleSpecificDateRequestState<TRequest> {
    const [request, setRequest] = useState<TRequest | null>(null);
    const requestRef = useRef<TRequest | null>(null);
    const settleRef = useRef<((isApplied: boolean) => void) | null>(null);

    const settle = useCallback((isApplied: boolean) => {
        settleRef.current?.(isApplied);
        settleRef.current = null;
        requestRef.current = null;
        setRequest(null);
    }, []);

    useEffect(
        () => () => {
            settleRef.current?.(false);
            settleRef.current = null;
        },
        [],
    );

    const open = useCallback((nextRequest: TRequest) => {
        settleRef.current?.(false);
        requestRef.current = nextRequest;
        setRequest(nextRequest);
        return new Promise<boolean>((resolve) => {
            settleRef.current = resolve;
        });
    }, []);

    const complete = useCallback(
        (completedRequest: TRequest) => {
            if (requestRef.current === completedRequest) {
                settle(true);
            }
        },
        [settle],
    );
    const cancel = useCallback(() => settle(false), [settle]);

    return useMemo(
        () => ({ request, open, complete, cancel }),
        [cancel, complete, open, request],
    );
}
